import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, Search, Filter, ArrowDownUp, Eye, Pencil, Power, PowerOff, MoreVertical, ShieldCheck, PauseCircle, PlayCircle, Plus } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable, TableColumn } from '@/components/ui/DataTable'
import { Paginator } from '@/components/ui/Paginator'
import { Select } from '@/components/ui/Select'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown'
import { useUserList } from '@/hooks/useUserList'
import { UserModel, UserRole } from '@/models/user.model'
import { usersService } from '@/services/usersService'
import { CreateUserModal } from '@/components/CreateUserModal'
import { useNotifications } from '@/contexts/NotificationContext'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/utils/cn'
import { paths } from '@/routes/path'

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

const ROLE_FILTERS = [
  { value: 'all', label: 'All roles' },
  { value: UserRole.ADMIN, label: 'Admins' },
  { value: UserRole.INSTRUCTOR, label: 'Instructors' },
  { value: UserRole.LEARNER, label: 'Learners' },
]

export function UsersPage() {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [pendingStatusUser, setPendingStatusUser] = useState<UserModel | null>(null)
  const [pendingStatus, setPendingStatus] = useState<'activate' | 'deactivate' | null>(null)
  const [bypassToastUser, setBypassToastUser] = useState<string | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const queryClient = useQueryClient()
  const { addNotification } = useNotifications()
  const { user: currentUser } = useAuth()
  const canManageStatuses = currentUser?.role === UserRole.ADMIN

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400)
    return () => clearTimeout(timeout)
  }, [searchTerm])

  const { data, isLoading, isFetching, isPlaceholderData } = useUserList({
    page,
    perPage,
    search: debouncedSearch || undefined,
    role: roleFilter === 'all' ? undefined : roleFilter,
    isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
  })

  const users = data?.data ?? []
  const meta = data?.meta
  const isStatusDialogOpen = Boolean(pendingStatusUser && pendingStatus)

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      return usersService.toggleUserStatus(userId, isActive)
    },
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
      if (updatedUser.id) {
        queryClient.invalidateQueries({ queryKey: ['users', 'detail', updatedUser.id] })
      }
      addNotification({
        title: 'User status updated',
        message: `${updatedUser.fullName || 'User'} is now ${updatedUser.isActive ? 'active' : 'inactive'}.`,
        type: 'general',
        priority: 'low',
        isRead: false,
        isArchived: false,
      })
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to update user status'
      addNotification({
        title: 'Status update failed',
        message,
        type: 'general',
        priority: 'medium',
        isRead: false,
        isArchived: false,
      })
    },
  })

  const paymentBypassMutation = useMutation({
    mutationFn: async ({ userId, bypass }: { userId: string; bypass: boolean; display?: string }) => {
      return usersService.setPaymentBypass(userId, bypass)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
      addNotification({
        title: 'Account access updated',
        message: `Student access ${variables.bypass ? 'enabled' : 'paused'}.`,
        type: 'general',
        priority: 'low',
        isRead: false,
        isArchived: false,
      })
      setBypassToastUser(variables.display ?? null)
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to update payment bypass'
      addNotification({
        title: 'Payment bypass failed',
        message,
        type: 'general',
        priority: 'medium',
        isRead: false,
        isArchived: false,
      })
    },
  })

  const isBypassUpdating = paymentBypassMutation.isPending

  useEffect(() => {
    if (!meta) return
    const totalPages = Math.max(1, Math.ceil(meta.total / meta.perPage))
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [meta, page])

  useEffect(() => {
    if (!bypassToastUser) return
    const timer = setTimeout(() => setBypassToastUser(null), 3500)
    return () => clearTimeout(timer)
  }, [bypassToastUser])

  const openStatusDialog = useCallback((user: UserModel) => {
    if (!user.id || toggleStatusMutation.isPending || !canManageStatuses) return
    setPendingStatusUser(user)
    setPendingStatus(user.isActive ? 'deactivate' : 'activate')
  }, [toggleStatusMutation.isPending, canManageStatuses])

  const handleConfirmStatusChange = useCallback(() => {
    if (!pendingStatusUser?.id || !pendingStatus || !canManageStatuses) return
    const nextStatus = pendingStatus === 'activate'
    toggleStatusMutation.mutate({ userId: pendingStatusUser.id, isActive: nextStatus })
    setPendingStatusUser(null)
    setPendingStatus(null)
  }, [pendingStatusUser, pendingStatus, toggleStatusMutation, canManageStatuses])

  const handleCancelStatusChange = useCallback(() => {
    setPendingStatusUser(null)
    setPendingStatus(null)
  }, [])

  const handleActionSelect = useCallback((user: UserModel, action: string) => {
    if (!user?.id || !action) return
    if (action === 'toggle-status') {
      if (!canManageStatuses) return
      openStatusDialog(user)
    } else if (action === 'pause-bypass') {
      setBypassToastUser(null)
      paymentBypassMutation.mutate({
        userId: user.id,
        bypass: false,
        display: user.fullName || user.email || user.id || 'Learner',
      })
    } else if (action === 'resume-bypass') {
      setBypassToastUser(null)
      paymentBypassMutation.mutate({
        userId: user.id,
        bypass: true,
        display: user.fullName || user.email || user.id || 'Learner',
      })
    }
  }, [openStatusDialog, paymentBypassMutation, canManageStatuses])

  const buildActionItems = useCallback((user: UserModel): DropdownItem[] => {
    const hasId = Boolean(user.id)
    const items: DropdownItem[] = [
      {
        label: 'View',
        icon: <Eye className="h-3.5 w-3.5" />,
        href: hasId ? paths.app.users.detail.build(user.id as string) : undefined,
        disabled: !hasId,
      },
      {
        label: 'Edit',
        icon: <Pencil className="h-3.5 w-3.5" />,
        href: hasId ? paths.app.users.edit.build(user.id as string) : undefined,
        disabled: !hasId,
      },
    ]

    if (canManageStatuses) {
      items.push({
        label: user.isActive ? 'Deactivate' : 'Activate',
        value: 'toggle-status',
        icon: user.isActive ? (
          <Power className="h-3.5 w-3.5 text-emerald-600" />
        ) : (
          <PowerOff className="h-3.5 w-3.5 text-secondary-500" />
        ),
        disabled: !hasId || toggleStatusMutation.isPending,
      })
    }

    if (user.role === UserRole.LEARNER && hasId) {
      items.push({
        label: 'Licenses',
        icon: <ShieldCheck className="h-3.5 w-3.5 text-primary-600" />,
        href: paths.app.users.licenses.build(user.id as string),
      })

      items.push(
        {
          label: 'Pause account',
          value: 'pause-bypass',
          icon: <PauseCircle className="h-3.5 w-3.5 text-amber-600" />,
          disabled: paymentBypassMutation.isPending,
        },
        {
          label: 'Resume account',
          value: 'resume-bypass',
          icon: <PlayCircle className="h-3.5 w-3.5 text-emerald-600" />,
          disabled: paymentBypassMutation.isPending,
        },
      )
    }

    return items
  }, [toggleStatusMutation.isPending, paymentBypassMutation.isPending, canManageStatuses])

  const columns: TableColumn<UserModel>[] = useMemo(() => {
    const formatDate = (value: string | null) => {
      if (!value) return '—'
      try {
        const date = new Date(value)
        return new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }).format(date)
      } catch {
        return '—'
      }
    }

    const roleBadgeColor = (role: UserRole | null) => {
      switch (role) {
        case UserRole.ADMIN:
          return 'bg-rose-50 text-rose-700'
        case UserRole.INSTRUCTOR:
          return 'bg-blue-50 text-blue-700'
        case UserRole.LEARNER:
          return 'bg-emerald-50 text-emerald-700'
        default:
          return 'bg-secondary-50 text-secondary-700'
      }
    }

    const statusBadgeColor = (isActive: boolean | null) =>
      isActive
        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
        : 'bg-secondary-100 text-secondary-600 border border-secondary-200'

    return [
      {
        key: 'serial',
        header: 'S/N',
        className: 'w-16',
        align: 'center' as const,
        render: (_, index: number) => ((page - 1) * perPage) + index + 1,
      },
      {
        key: 'name',
        header: 'Name',
        render: (user) => (
          <div className="flex items-center gap-3">
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={user.fullName}
                className="h-10 w-10 rounded-full object-cover ring-2 ring-secondary-100"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-sm font-semibold text-secondary-600">
                {user.initials || <Users className="h-4 w-4" />}
              </div>
            )}
            <div>
              <p className="font-semibold text-secondary-900">{user.fullName || 'Unnamed user'}</p>
              <p className="text-sm text-secondary-500">{user.email || '—'}</p>
            </div>
          </div>
        ),
      },
      {
        key: 'role',
        header: 'Role',
        render: (user) => (
          <Badge className={cn('rounded-full px-3 py-1 text-xs font-semibold capitalize', roleBadgeColor(user.role))}>
            {user.role ?? '—'}
          </Badge>
        ),
      },
      {
        key: 'department',
        header: 'Course/Department',
        render: (user) => (
          <span className="text-sm text-secondary-700">
            {user.programmeTitle || user.departmentName || '—'}
          </span>
        ),
      },
      {
        key: 'created',
        header: 'Created',
        render: (user) => (
          <span className="text-sm text-secondary-600">{formatDate(user.createdAt)}</span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (user) => (
          <Badge className={cn('rounded-full px-3 py-1 text-xs font-semibold capitalize', statusBadgeColor(user.isActive))}>
            {user.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        className: 'w-36',
        render: (user: UserModel) => (
          <Dropdown
            triggerLabel={
              <span className="flex items-center gap-2 text-secondary-700">
                <MoreVertical className="h-4 w-4" />
                Actions
              </span>
            }
            className="rounded-full border border-secondary-200 px-3 py-1 text-secondary-600 hover:bg-secondary-50"
            items={buildActionItems(user)}
            onSelect={(value) => handleActionSelect(user, value)}
            align="end"
            width="w-56"
            hideArrow
          />
        ),
      },
    ]
  }, [page, perPage, buildActionItems, handleActionSelect])

  return (
    <>
      <div className="space-y-6">
        <Card className="border-none bg-transparent shadow-none">
          <CardHeader className="px-0 pt-0">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <CardTitle className="text-2xl font-bold text-secondary-900">Role Management</CardTitle>
                <CardDescription className="mt-1 text-secondary-600">
                  Monitor, review, and update members of the Kaduna Business School community.
                </CardDescription>
              </div>
              {canManageStatuses && (
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  <Plus className="h-4 w-4" />
                  Add Members
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent className="px-0">
            <div className="flex flex-col gap-4 rounded-2xl border border-secondary-100 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
              <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400 z-10" />
                  <Input
                    value={searchTerm}
                    onChange={(event) => {
                      setSearchTerm(event.target.value)
                      setPage(1)
                    }}
                    placeholder="Search users by name or email"
                    className="pl-10"
                  />
                </div>
                <Select
                  value={roleFilter}
                  onChange={(event) => {
                    setRoleFilter(event.target.value)
                    setPage(1)
                  }}
                  className="w-full sm:w-48"
                >
                  {ROLE_FILTERS.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </Select>
                <Select
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value)
                    setPage(1)
                  }}
                  className="w-full sm:w-48"
                >
                  {STATUS_FILTERS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="text-secondary-600 hover:text-secondary-900">
                  <ArrowDownUp className="mr-2 h-4 w-4" />
                  Sort
                </Button>
                <Button variant="ghost" size="sm" className="text-secondary-600 hover:text-secondary-900">
                  <Filter className="mr-2 h-4 w-4" />
                  Filter
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <DataTable<UserModel>
          data={users}
          columns={columns}
          isLoading={isLoading && !isFetching}
          emptyState={
            <div className="py-10 text-center">
              <Users className="mx-auto h-10 w-10 text-secondary-300" />
              <h3 className="mt-3 text-lg font-semibold text-secondary-900">No users found</h3>
              <p className="mt-1 text-sm text-secondary-500">
                Adjust filters or try searching with a different keyword.
              </p>
            </div>
          }
          getRowId={(user) => user.id ?? `${user.email}-${user.createdAt}`}
        />

        <Paginator
          meta={meta}
          page={page}
          perPage={perPage}
          onPageChange={(nextPage) => setPage(Math.max(1, nextPage))}
          onPerPageChange={(nextPerPage) => {
            setPerPage(nextPerPage)
            setPage(1)
          }}
          isPreviousData={isPlaceholderData || isFetching}
        />
      </div>
      <ConfirmDialog
        open={isStatusDialogOpen}
        title={pendingStatus === 'activate' ? 'Activate user?' : 'Deactivate user?'}
        description={
          pendingStatusUser
            ? `Are you sure you want to ${pendingStatus} ${pendingStatusUser.fullName || 'this user'}?`
            : undefined
        }
        confirmLabel={pendingStatus === 'activate' ? 'Activate' : 'Deactivate'}
        confirmTone={pendingStatus === 'activate' ? 'default' : 'destructive'}
        onConfirm={handleConfirmStatusChange}
        onCancel={handleCancelStatusChange}
      />

      {/* Bypass status feedback */}
      {isBypassUpdating && (
        <div className="fixed bottom-4 right-4 rounded-md bg-secondary-900/90 px-4 py-3 text-sm text-white shadow-lg">
          Updating account access…
        </div>
      )}
      {bypassToastUser && !isBypassUpdating && (
        <div className="fixed bottom-4 right-4 rounded-md bg-emerald-600 px-4 py-3 text-sm text-white shadow-lg">
          Account access updated for {bypassToastUser}.
        </div>
      )}

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
        }}
        defaultRole={
          roleFilter && roleFilter !== 'all' ? roleFilter : undefined
        }
      />
    </>
  )
}
